import { useNavigate } from 'react-router-dom';
import useUserQuery from '../../hooks/queries/useUserQuery';
import { useGetUserIdentities } from '../../../domains/user/ui/hooks/useGetUserIdentities';
import { useBilling } from './useBilling';
import type { BillingKeyIssueRequest } from '../../../domain/models/payments/Billing';
import {
  isBillingBuyerInfoComplete,
  loadBillingBuyerInfo
} from '../../../utils/billingBuyerInfoStorage';

interface UseInicisBillingPaymentProps {
  amount: string;
  productName: string;
  productId: string;
}

export const useInicisBillingPayment = ({
  amount,
  productName,
  productId
}: UseInicisBillingPaymentProps) => {
  const navigate = useNavigate();
  const { data: userIdentity, isLoading: isLoadingUserIdentities } = useGetUserIdentities();
  const { generateOrderId, getCurrentTimestamp, issueBillingKey } = useBilling();
  const { data: user, isLoading: isLoadingUser } = useUserQuery();

  const handleCreateOrder = async () => {
    if (!productId || isLoadingUser) return;
    try {
      const userBuyerInfo = userIdentity
        ? {
            buyerName: userIdentity.name,
            buyerEmail: userIdentity.email,
            buyerTel: userIdentity.phoneNumber
          }
        : null;
      const storedBuyerInfo = loadBillingBuyerInfo();
      const resolvedBuyerInfo = isBillingBuyerInfoComplete(userBuyerInfo)
        ? userBuyerInfo
        : isBillingBuyerInfoComplete(storedBuyerInfo)
          ? storedBuyerInfo
          : null;

      if (!resolvedBuyerInfo) {
        if (!isLoadingUserIdentities) {
          navigate(
            `/payment/buyer-info?amount=${amount}&productName=${productName}&productId=${productId}&isBilling=true`
          );
        }
        return;
      }

      const orderId = await generateOrderId(productId);

      const payload: BillingKeyIssueRequest = {
        productId: productId,
        buyerName: resolvedBuyerInfo.buyerName,
        buyerEmail: resolvedBuyerInfo.buyerEmail,
        buyerTel: resolvedBuyerInfo.buyerTel,
        goodName: productName,
        price: amount.toString(),
        orderId: orderId,
        timestamp: getCurrentTimestamp(),
        customData: user?.userId,
        merchantRedirectData: user?.userId
      };

      await issueBillingKey(payload);
    } catch {
      navigate('/payment/fail');
    }
  };

  return {
    handleCreateOrder,
    user
  };
};
